import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import styles from "../../../styles/components/Modal/LeadSettings/AddLeadModal.module.css";
import { InputSelector } from "../../UI/InputSelector";
import { DropdownSelector } from "../../UI/DropdownSelector";
import CloseIcon from "@mui/icons-material/Close";
import { fieldsConfigAdmin, fieldsConfigAffiliator } from "../../../utils/forms/leadFields";
import { fetchFilterOptions } from "../../../utils/api/fetchLeadFilter";
import { createLead } from "../../../utils/api/Lead/createLead";
import { Notification } from "../../UI/Notifications";
import { getAdmins } from "../../../utils/api/Admin/getAdminsApi";
import { getAllDesksApi } from "../../../utils/api/Admin/desk/getAllDesksApi";
import { getAffiliate } from "../../../utils/api/Affiliate/getAffiliateApi";
import { handleError } from "../../../utils/custom/errorHandler";
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";
import { fieldLabels } from "../../../utils/forms/fieldsLabels";

const translations = {
    ru: {
        title: "Создать лида",
        confirm: "Подтвердить",
        errorFetchingManagers: "Ошибка при получении менеджеров:",
        errorFetchingDesks: "Ошибка при получении списка десков:",
        errorFetchingAffiliates: "Ошибка при получении списка аффилиатов:",
        errorFetchingOptions: (key) => `Ошибка при получении опций для ${key}:`,
    },
    en: {
        title: "Create Lead",
        confirm: "Confirm",
        errorFetchingManagers: "Error fetching managers:",
        errorFetchingDesks: "Error fetching desks:",
        errorFetchingAffiliates: "Error fetching affiliates:",
        errorFetchingOptions: (key) => `Error fetching options for ${key}:`,
    },
};

export const AddLeadModal = ({ setModalAddLead, role, onLeadCreated }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;
    const tFields = fieldLabels[langSelected] || fieldLabels.ru;

    const modalRef = useRef(null);
    const fieldsConfig = useMemo(() => (role === "admin" ? fieldsConfigAdmin : fieldsConfigAffiliator), [role]);
    const [formData, setFormData] = useState(
        fieldsConfig.reduce((acc, field) => ({ ...acc, [field.key]: "" }), {})
    );
    const [dropdownOptions, setDropdownOptions] = useState({});
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const inputFieldsConfig = useMemo(() => fieldsConfig.filter((field) => field.type === "input" && field.addStatus === "active"), [fieldsConfig]);
    const dropdownFieldsConfig = useMemo(() => fieldsConfig.filter((field) => field.type === "dropdown" && field.addStatus === "active"), [fieldsConfig]);

    useEffect(() => {
        const loadDropdownOptions = async () => {
            const options = {};
            await Promise.all(
                dropdownFieldsConfig.map(async (field) => {
                    if (field.key === 'manager_id') {
                        try {
                            const admins = await getAdmins();
                            options[field.key] = admins.data.map(admin => ({
                                value: admin.id,
                                label: `${admin.first_name} ${admin.last_name}`,
                            }));
                        } catch (error) {
                            console.error(t.errorFetchingManagers, error);
                            options[field.key] = [];
                        }
                    } else if (field.key === 'desk_id') {
                        try {
                            const desksData = await getAllDesksApi();
                            options[field.key] = desksData.data.map(desk => ({
                                value: desk.id,
                                label: `${desk.name || ''} (${desk.description || ''})`.trim(),
                                name: desk.name,
                            }));
                        } catch (error) {
                            console.error(t.errorFetchingDesks, error);
                            options[field.key] = [];
                        }
                    } else if (field.key === 'affiliator_id') {
                        try {
                            const affiliates = await getAffiliate();
                            options[field.key] = affiliates.data.map(affiliate => ({
                                value: affiliate.id,
                                label: affiliate.first_name || affiliate.last_name
                                    ? `${affiliate.first_name || ''} ${affiliate.last_name || ''}`.trim()
                                    : affiliate.email,
                            }));
                        } catch (error) {
                            console.error(t.errorFetchingAffiliates, error);
                            options[field.key] = [];
                        }
                    } else if (field.endpoint) {
                        try {
                            const data = await fetchFilterOptions(field.endpoint, langSelected);
                            options[field.key] = data || [];
                        } catch (error) {
                            console.error(t.errorFetchingOptions(field.key), error);
                            options[field.key] = [];
                        }
                    }
                })
            );
            setDropdownOptions(options);
        };

        loadDropdownOptions();
    }, [dropdownFieldsConfig, role, t, langSelected]);

    const handleChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        if (touched[key]) {
            setErrors((prev) => ({ ...prev, [key]: "" }));
        }
    };

    const handleFocus = (key) => {
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const handleBlur = (key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const handleSubmit = async () => {
        const payload = { ...formData };
        if (role === "admin" && payload.desk) {
            payload.desk_id = Number(payload.desk);
            delete payload.desk;
        }
        try {
            await createLead(payload, role);
            if (onLeadCreated) {
                onLeadCreated();
            }
        } catch (err) {
            handleError(err, setErrors);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setModalAddLead(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setModalAddLead]);

    const modalClass = `${styles.modal} ${theme === 'dark' ? styles.darkModal : ""}`;
    const containerClass = `${styles.filtersContainer} ${theme === 'dark' ? styles.darkFiltersContainer : ""}`;
    const labelClass = `${styles.modalLabel} ${theme === 'dark' ? styles.darkModalLabel : ""}`;
    const footerClass = `${styles.modalFooter} ${theme === 'dark' ? styles.darkModalFooter : ""}`;
    const submitBtnClass = `${styles.submitButton} ${theme === 'dark' ? styles.darkSubmitButton : ""}`;

    return (
        <div className={modalClass}>
            {errors.general && <Notification message={errors.general} type={"error"} />}
            <div className={containerClass} ref={modalRef}>
                <div className={labelClass}>
                    <p>{t.title}</p>
                    <div onClick={() => setModalAddLead(false)}>
                        <CloseIcon className={theme === 'dark' ? styles.darkCloseIcon : ""} />
                    </div>
                </div>
                <div className={styles.modalContent}>
                    {inputFieldsConfig.map((field) => (
                        <div key={field.key} className={styles.inputRow}>
                            <label className={theme === 'dark' ? styles.darkText : ""}>{tFields[field.key]}:</label>
                            <InputSelector
                                filterName={field.key}
                                selectedValue={formData[field.key]}
                                handleFilterChange={handleChange}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                hasError={!!errors[field.key]}
                                errorMessage={errors[field.key]}
                                isTouched={touched[field.key]}
                                theme={theme}
                            />
                        </div>
                    ))}
                    {dropdownFieldsConfig.map((field) => (
                        <div key={field.key} className={styles.inputRow}>
                            <label className={theme === 'dark' ? styles.darkText : ""}>{tFields[field.key]}:</label>
                            <DropdownSelector
                                filterName={field.key}
                                selectedValue={formData[field.key]}
                                handleFilterChange={handleChange}
                                options={dropdownOptions[field.key] || []}
                                hasError={!!errors[field.key]}
                                errorMessage={errors[field.key]}
                                isTouched={touched[field.key]}
                                onFocus={() => handleFocus(field.key)}
                                onBlur={() => handleBlur(field.key)}
                                theme={theme}
                            />
                        </div>
                    ))}
                </div>
                <div className={footerClass}>
                    <div className={styles.confirmationWrapper}>
                        <button className={submitBtnClass} onClick={handleSubmit}>
                            {t.confirm}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
